import React, { useState } from 'react';

import DefaultProfileImage from 'assets/icons/Profile.svg';
import ListingName from 'parent-portal/providers/cards/SearchListing/ListingName';
import DocumentLinks from 'parent-portal/provider-profile/tabs/documents/DocumentShared';
import DownloadAllDocuments from 'parent-portal/provider-profile/tabs/documents/DownloadAllDocuments';
import PreferWorkerModal from 'parent-portal/shared/UserActions/PreferWorkerModal';
import { PromptForReviewModal } from 'parent-portal/shared/UserActions/PromptForReviewModal';

import { Avatar, Checkbox, Chip, Colors, IconButton, Paper, Text } from 'library';
import { Collapse, styled } from '@mui/material';
import { PermissionType, useUserContext } from 'UserContext';

import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import type { BusinessLocationPreferredWorker } from 'models';
import type { IProviderSearch } from '../models';
import { PromptType } from 'parent-portal/shared/UserActions/PromptForReviewModal/PromptForReviewModal';

function formatLastShiftDate(dateString?: string | null): string | null {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function ProviderRow({
    provider,
    checked,
    handleCheck,
}: {
    provider: IProviderSearch;
    checked: boolean;
    handleCheck: (checked: boolean) => void;
}) {
    const userAvatar = provider.profile_image?.image;

    const { user, hasPermission } = useUserContext();
    const canPostJobs = hasPermission(PermissionType.PostJob);
    const businessId = user?.businesses_active?.[0].id;

    const [openPreferredModal, setOpenPreferredModal] = useState<boolean>(false);
    const [openPromptForReviewModal, setOpenPromptForReviewModal] = useState<boolean>(false);
    const [promptType, setPromptType] = useState<PromptType>();
    const [docsExpanded, setDocsExpanded] = useState<boolean>(false);

    const [preferredLocations, setPreferredLocations] = useState<number>(provider.preferred_workers?.length ?? 0);
    const [preferredAll, setPreferredAll] = useState<boolean>(
        !!provider.preferred_workers?.length && !provider.preferred_workers?.[0]?.business_location,
    );

    const isEligible = provider.hours_worked >= provider.total_trial_run_hours;
    const isPreferred = preferredAll || preferredLocations > 0;
    const isBlocked = (provider.business_outlaws?.length ?? 0) > 0;

    function handlePrefer(preferredWorkers: BusinessLocationPreferredWorker[], prompt: boolean = false) {
        const preferred: number = preferredWorkers.reduce(
            (total, preferredWorker) => total + (preferredWorker.is_preferred_worker ? 1 : 0),
            0,
        );
        const allPreferred = preferred === preferredWorkers.length;

        setPreferredAll(allPreferred);
        setPreferredLocations(preferred);
        setPromptType('prefer');
        setOpenPromptForReviewModal(prompt);
    }

    const preferredLabel = preferredAll
        ? 'all locations'
        : `${preferredLocations} location${preferredLocations !== 1 ? 's' : ''}`;
    const blockedLabel =
        !!provider.business_outlaws?.length && !provider.business_outlaws?.[0]?.business_location
            ? 'all locations'
            : `${provider.business_outlaws?.length ?? 0} location${(provider.business_outlaws?.length ?? 0) !== 1 ? 's' : ''}`;

    const lastShiftFormatted = formatLastShiftDate(provider.last_shift_date);

    return (
        <>
            <CardWrapper>
                <Paper borderRadius={12} padding={16} style={{ marginBottom: 12 }}>
                    <CardTopRow>
                        {canPostJobs && (
                            <CheckboxContainer>
                                <Checkbox label="" checked={checked} onChange={handleCheck} />
                            </CheckboxContainer>
                        )}
                        <AvatarContainer>
                            {userAvatar ? (
                                <Avatar src={userAvatar} style={{ height: 56, width: 56 }} />
                            ) : (
                                <StyledProfileImage />
                            )}
                        </AvatarContainer>
                        <InfoContainer>
                            <ListingName provider={provider} />
                            <DetailsRow>
                                {isEligible ? (
                                    <Text variant="body2" bold textStyle={{ color: Colors.turquoise }}>
                                        {provider.total_trial_run_hours} hours — Eligible for hire
                                    </Text>
                                ) : (
                                    <Text variant="body2" textStyle={{ color: Colors.mediumNavy }}>
                                        {provider.hours_worked.toFixed(1)} / {provider.total_trial_run_hours} hours
                                        completed
                                    </Text>
                                )}
                                {lastShiftFormatted && (
                                    <>
                                        <Separator />
                                        <Text variant="caption" textStyle={{ color: Colors.mediumGrey }}>
                                            Last shift: {lastShiftFormatted}
                                        </Text>
                                    </>
                                )}
                            </DetailsRow>
                            <TagsRow>
                                {isPreferred && (
                                    <Chip
                                        label={`Preferred — ${preferredLabel}`}
                                        backgroundColor={Colors.lightTurq}
                                        textColor={Colors.turquoise}
                                        style={{ height: 22, fontSize: 11 }}
                                    />
                                )}
                                {isBlocked && (
                                    <Chip
                                        label={`Blocked — ${blockedLabel}`}
                                        backgroundColor={Colors.lightRed}
                                        textColor={Colors.red}
                                        style={{ height: 22, fontSize: 11 }}
                                    />
                                )}
                            </TagsRow>
                        </InfoContainer>
                        <ActionsContainer>
                            <IconButton
                                icon={isPreferred ? StarIcon : StarBorderIcon}
                                color={isPreferred ? Colors.turquoise : Colors.mediumNavy}
                                tooltip={isPreferred ? 'Edit preferred status' : 'Add as preferred'}
                                onClick={() => setOpenPreferredModal(true)}
                                size={32}
                                style={{ boxShadow: 'none' }}
                            />
                            <DownloadAllContainer>
                                <DownloadAllDocuments provider={provider} />
                            </DownloadAllContainer>
                            <IconButton
                                icon={docsExpanded ? ExpandLessIcon : ExpandMoreIcon}
                                color={Colors.mediumNavy}
                                tooltip={docsExpanded ? 'Hide documents' : 'Show documents'}
                                onClick={() => setDocsExpanded(!docsExpanded)}
                                size={32}
                                style={{ boxShadow: 'none' }}
                            />
                        </ActionsContainer>
                    </CardTopRow>
                    <Collapse in={docsExpanded}>
                        <DocsContainer>
                            <DocumentLinks provider={provider} />
                        </DocsContainer>
                    </Collapse>
                </Paper>
            </CardWrapper>
            {businessId && (
                <PreferWorkerModal
                    open={openPreferredModal}
                    provider={{
                        id: provider.user.id,
                        first_name: provider.user.first_name,
                        last_name: provider.user.last_name,
                        profile_picture: { image: userAvatar ?? '' },
                    }}
                    onSuccess={handlePrefer}
                    onClose={() => setOpenPreferredModal(false)}
                />
            )}
            {businessId && (
                <PromptForReviewModal
                    open={openPromptForReviewModal}
                    onClose={() => {
                        setOpenPromptForReviewModal(false);
                        setPromptType(undefined);
                    }}
                    userId={provider.user.id}
                    promptedByActionType={promptType}
                />
            )}
        </>
    );
}

const CardWrapper = styled('div')({});

const CardTopRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 16,
});

const CheckboxContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
});

const AvatarContainer = styled('div')({
    flexShrink: 0,
});

const StyledProfileImage = styled(DefaultProfileImage)({
    height: 56,
    width: 56,
    borderRadius: '50%',
    flexShrink: 0,
});

const InfoContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    gap: 4,
});

const DetailsRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
});

const Separator = styled('span')({
    color: Colors.lightGrey,
    margin: '0 4px',
    '&::before': {
        content: '"·"',
    },
});

const TagsRow = styled('div')({
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
});

const ActionsContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
});

const DownloadAllContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
});

const DocsContainer = styled('div')({
    paddingTop: 12,
    paddingLeft: 88,
    paddingBottom: 4,
});
